import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface CrmActivityRow {
  interaction_id: string;
  salesperson_id: string;
  user_id: string;
  entity_name: string;
  contact_name: string;
  interaction_date: string;
  interaction_type: string;
  subject: string;
  duration_minutes: number;
  outcome: string;
  follow_up_required: boolean;
  opportunity_id: string | null;
  opportunity_title: string | null;
}

export interface CrmActivitySalespersonGroup {
  salesperson_id: string;
  rows: CrmActivityRow[];
  totalDuration: number;
  typeCounts: { [key: string]: number };
}

@Component({
  selector: 'app-crm-activity-report',
  templateUrl: './crm-activity-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class CrmActivityReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Salesperson Activity Report';
  isLoading = false;
  noDataFound = false;
  rows: CrmActivityRow[] = [];
  groups: CrmActivitySalespersonGroup[] = [];
  users: any[] = [];
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    salesperson_id: '',
    interaction_type: '',
    from_date: '',
    to_date: '',
  };

  interactionTypeOptions = [
    {value: '', label: '— All —'},
    {value: 'call', label: 'Call'},
    {value: 'email', label: 'Email'},
    {value: 'meeting', label: 'Meeting'},
    {value: 'demo', label: 'Demo'},
    {value: 'site_visit', label: 'Site Visit'},
    {value: 'note', label: 'Note'},
  ];

  vm: any = {Preferences: []};

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';

    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.users = result.Data ?? result.items ?? [];
      },
    });
  }

  getUserName(userId: string): string {
    if (!userId) {
      return '—';
    }
    const u = this.users.find(x => x.id === userId);
    return u ? u.name : userId;
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      salesperson_id: '',
      interaction_type: '',
      from_date: '',
      to_date: '',
    };
    this.rows = [];
    this.groups = [];
    this.vm.report = null;
  }

  close() {
    this.router.navigate(['/home']);
  }

  runReport() {
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];
    this.groups = [];

    let params = 'company_id=' + (this.filters.company_id || '');
    if (this.filters.salesperson_id) {
      params += '&salesperson_id=' + this.filters.salesperson_id;
    }
    if (this.filters.interaction_type) {
      params += '&interaction_type=' + this.filters.interaction_type;
    }
    const fd = this.dateFilter.GetDateFormat(this.filters.from_date);
    const td = this.dateFilter.GetDateFormat(this.filters.to_date);
    if (fd) {
      params += '&from_date=' + fd;
    }
    if (td) {
      params += '&to_date=' + td;
    }

    this.tableDataService.getDataWithParams('CrmActivityReport', params).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : (result.Data ?? result.items ?? []);
        this.buildGroups();
        this.noDataFound = this.rows.length === 0;
        this.vm.report = this.rows;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  private buildGroups() {
    const map = new Map<string, CrmActivityRow[]>();
    for (const r of this.rows) {
      const key = r.salesperson_id || r.user_id;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(r);
    }
    this.groups = Array.from(map.entries()).map(([spId, rows]) => {
      const typeCounts: { [key: string]: number } = {};
      let totalDuration = 0;
      for (const r of rows) {
        typeCounts[r.interaction_type] = (typeCounts[r.interaction_type] || 0) + 1;
        totalDuration += r.duration_minutes ?? 0;
      }
      return {salesperson_id: spId, rows, totalDuration, typeCounts};
    });
  }

  typeCountSummary(typeCounts: { [key: string]: number }): string {
    return Object.entries(typeCounts).map(([k, v]) => `${k}: ${v}`).join(', ');
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('CRM Activity Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `CrmActivityReport_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
